import type { FC } from "react";
import Link from "next/link";

import type {
  TArticleCardProps,
} from "@/ui/components/data-display/Card/ArticleCard/ArticleCard.config";
import {
  Article,
  HorizontalRule,
  Inline,
  Stack,
} from "@/ui/components/layout";
import { AspectRatio, Badge, Image } from "@/ui/components/primitives";
import { TextBody } from "@/ui/components/typography/TextBody";
import { TextControl } from "@/ui/components/typography/TextControl";
import { TextSubtitle } from "@/ui/components/typography/TextSubtitle";
import { SHORTHAND_MONTH_DAY_YEAR } from "libs/utils/misc/src/dates";
import { useTranslations } from "next-intl";

export const ArticleCard: FC<TArticleCardProps> = ({
  image,
  categories,
  title,
  description,
  publishedAt,
  readTime,
  author,
  href,
}) => {

  const t = useTranslations("Articles");

  return (
    <Article
      data-component="ArticleCard"
      className="flex h-full w-full flex-col overflow-hidden rounded-sys-sm border bg-sys-white border-solid border-component-card-base shadow-sm"
    >
      <AspectRatio ratio="4:3" className="bg-state-loading">
        { image ? (
          <Image {...image} fit="cover" />
        ) : null }
      </AspectRatio>
      <div className="flex flex-1 flex-col p-4">
        <Stack gap="2">
          <Inline gap="2">
            { categories.map((category, index) => (
              <Badge key={index} variant="secondary" {...category} />
            )) }
          </Inline>
          <div className="text-foreground-muted-strong">
            <TextBody
              size={3}
              isResponsive={false}
              color="inherit"
            >
              { (publishedAt).format(SHORTHAND_MONTH_DAY_YEAR) } · { t("readTime", { readTime }) }
            </TextBody>
          </div>
          <Link href={href}>
            <TextSubtitle tag="h3" size={2} isResponsive={false}>
              { title }
            </TextSubtitle>
          </Link>
          <TextBody size={3} isResponsive={false}>
            { description }
          </TextBody>
        </Stack>
        <div className="mt-auto pt-4">
          <HorizontalRule />
        </div>
        <Inline gap="2" className="pt-4">
          <div className="w-8">
            <AspectRatio ratio="3:4" className="bg-state-loading">
              { author.image ? (
                <Image {...author.image} fit="cover" />
              ) : null }
            </AspectRatio>
          </div>
          <div className="flex flex-col flex-1 justify-between">
            <TextControl size={2} isResponsive={false}>
              { author.name }
            </TextControl>
            <TextBody size={3} isResponsive={false}>
              { author.description }
            </TextBody>
          </div>
        </Inline>
      </div>
    </Article>
  );
};

ArticleCard.displayName = "ArticleCard";
